import React from 'react';
import { MapPin, Phone, Mail, Clock, CalendarCheck } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { businessTemplates } from '../../data/rubros';

/**
 * Encabezado de la página pública de un negocio.
 *
 * El hero anterior mostraba solo el nombre y tres círculos con emoji cuyo texto
 * vivía únicamente en el atributo `title`, o sea invisible en un teléfono. No
 * mostraba logo, descripción, dirección ni horarios, aunque la API los devuelve
 * y el CSS ya tenía clases muertas para ellos (.business-description-hero,
 * .hero-hours-card). Era la razón principal de que la página se viera barata.
 *
 * El color sale de --brand-primary (lo escribe lib/branding.js con el color que
 * eligió el negocio); la estructura, del sistema de Aturno.
 */

const DIAS = [
  ['monday', 'Lunes'],
  ['tuesday', 'Martes'],
  ['wednesday', 'Miércoles'],
  ['thursday', 'Jueves'],
  ['friday', 'Viernes'],
  ['saturday', 'Sábado'],
  ['sunday', 'Domingo'],
];

/** Iniciales para cuando el negocio no subió logo. */
function iniciales(nombre = '') {
  return nombre
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

/** Horario de hoy, en el formato que usa el onboarding: { enabled, ranges[] }. */
function horarioDeHoy(schedule) {
  if (!schedule) return null;
  const clave = DIAS[(new Date().getDay() + 6) % 7][0]; // getDay(): 0 = domingo
  const dia = schedule[clave];
  if (!dia || !dia.enabled || !dia.ranges?.length) return { abierto: false };
  return {
    abierto: true,
    rangos: dia.ranges.map((r) => `${r.start} a ${r.end}`).join(' y '),
  };
}

export default function HeroNegocio({ businessData, onManageBooking }) {
  const info = businessData.businessInfo || {};
  // `businessInfo` primero, igual que en las otras cinco líneas de acá abajo y
  // que en el panel. Estaba al revés SÓLO para el nombre, y la raíz gana: ahí
  // vive el nombre que se escribió al REGISTRARSE ("matias"), que el onboarding
  // nunca pisa —guarda en `businessInfo`—. Resultado: el negocio ponía "Santa
  // María de la Salud" en el onboarding, lo veía bien en el panel, y su página
  // pública —la que le pasa a sus clientes— salía encabezada con su nombre de
  // pila. La dirección, dos líneas más abajo, ya estaba en este orden, y por eso
  // esa sí se veía.
  const nombre = info.name || businessData.name || 'Nombre no disponible';
  const direccion = info.address || businessData.address || '';
  const telefono = info.phone || businessData.phone || '';
  const email = info.email || businessData.email || '';
  const descripcion = businessData.description || '';
  const logo = businessData.profileImage || businessData.logo || '';
  const hoy = horarioDeHoy(businessData.schedule);

  // El rubro ya está guardado en el negocio desde el onboarding y nadie lo
  // mostraba nunca. Le dice al cliente de qué se trata el lugar de un vistazo.
  const rubro = businessTemplates.find((t) => t.id === businessData.businessType);

  const contactos = [
    direccion && {
      icono: MapPin,
      texto: direccion,
      href: `https://maps.google.com/?q=${encodeURIComponent(direccion)}`,
      externo: true,
    },
    telefono && {
      icono: Phone,
      texto: telefono,
      href: `https://wa.me/${telefono.replace(/\D/g, '')}`,
      externo: true,
    },
    email && { icono: Mail, texto: email, href: `mailto:${email}`, externo: false },
  ].filter(Boolean);

  return (
    <section className="border-b border-neutral-200 bg-white">
      {/* Franja con el color del negocio. Es lo único que usa su color a full;
          el resto de la página lo usa como acento. */}
      <div
        className="h-28 w-full sm:h-36"
        style={{
          background:
            'linear-gradient(135deg, var(--brand-primary, #ff5722) 0%, var(--brand-secondary, #c2185b) 100%)',
        }}
      />

      <div className="mx-auto max-w-5xl px-4 pb-8 sm:px-6">
        {/* El avatar monta sobre la franja */}
        <div className="-mt-12 flex flex-col gap-5 sm:-mt-14 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-end gap-4">
            <Avatar className="h-24 w-24 border-4 border-white shadow-sm sm:h-28 sm:w-28">
              {logo && <AvatarImage src={logo} alt="" className="object-cover" />}
              <AvatarFallback
                className="text-2xl font-semibold text-white"
                style={{ background: 'var(--brand-primary, #ff5722)' }}
              >
                {iniciales(nombre)}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* `min-h-11` son 44px, que es el mínimo táctil de WCAG 2.5.5 y de
              las guías de Apple. El tamaño por defecto del botón deja 40, y
              medido en la página real éste era el único elemento de toda la
              pantalla por debajo del mínimo. Cuatro píxeles suenan a nada, pero
              es el botón que aprieta quien viene con el turno ya sacado —o sea
              alguien apurado, parado, con una mano— y es el que más caro sale
              errar: si no lo encuentra, escribe o llama. */}
          <Button
            variant="outline"
            onClick={onManageBooking}
            className="min-h-11 w-full gap-2 sm:mb-2 sm:w-auto"
          >
            <CalendarCheck className="h-4 w-4" />
            Ya tengo un turno
          </Button>
        </div>

        <div className="mt-5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
              {nombre}
            </h1>
            {rubro && (
              <Badge variant="secondary" className="gap-1.5">
                <span aria-hidden="true">{rubro.icon}</span>
                {rubro.name}
              </Badge>
            )}
          </div>

          {descripcion && (
            <p className="mt-3 max-w-2xl text-pretty leading-relaxed text-neutral-600">
              {descripcion}
            </p>
          )}

          <div className="mt-5 flex flex-col gap-3 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6">
            {hoy && (
              <span className="inline-flex items-center gap-2 text-neutral-700">
                <Clock className="h-4 w-4 shrink-0 text-neutral-400" />
                {hoy.abierto ? (
                  <>
                    <span className="font-medium text-green-700">Hoy abierto</span>
                    <span className="text-neutral-500">{hoy.rangos}</span>
                  </>
                ) : (
                  <span className="font-medium text-neutral-500">Hoy cerrado</span>
                )}
              </span>
            )}

            {contactos.map(({ icono: Icono, texto, href, externo }) => (
              <a
                key={texto}
                href={href}
                {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="inline-flex items-center gap-2 text-neutral-700 underline-offset-4 transition-colors hover:text-neutral-900 hover:underline"
              >
                <Icono className="h-4 w-4 shrink-0 text-neutral-400" />
                {texto}
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
